import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { getJurisdicciones } from "../../../services/matchingBancario.service.js";

/**
 * Hook centralizado para cargar opciones de filtros (jurisdicciones)
 * Reutilizable en cualquier componente que necesite estas opciones
 *
 * Optimizado para evitar re-renders innecesarios cuando las queries fallan
 */
export const useFilterOptions = () => {
    // Cargar jurisdicciones desde API
    const {
        data: jurisdiccionesData,
        isLoading: jurisdiccionesLoading,
        isError: jurisdiccionesError,
    } = useQuery({
        queryKey: ["jurisdicciones"],
        queryFn: getJurisdicciones,
        staleTime: 5 * 60 * 1000, // 5 minutos
        retry: false, // No reintentar automáticamente para evitar re-renders continuos
        placeholderData: [], // Mantener array vacío estable durante carga/error
    });

    // Usar valores estables para evitar re-renders
    const jurisdicciones = jurisdiccionesData || [];

    // Transformar jurisdicciones al formato esperado por los componentes
    const jurisdiccionesOptions = useMemo(() => {
        return jurisdicciones.map((j) => ({
            value: j.id,
            label: j.descripcion,
        }));
    }, [jurisdicciones]);

    // Función helper para obtener el label de una jurisdicción por su ID
    // Memoizada para mantener referencia estable
    const getJurisdiccionLabel = useMemo(() => {
        return (jurisdiccionId) => {
            const data = jurisdiccionesData || [];
            const jurisdiccion = data.find(j => j.id === jurisdiccionId);
            return jurisdiccion ? jurisdiccion.descripcion : jurisdiccionId;
        };
    }, [jurisdiccionesData]);

    return {
        // Datos
        jurisdicciones: jurisdiccionesOptions,

        // Estados de carga
        jurisdiccionesLoading,
        isLoading: jurisdiccionesLoading,

        // Estados de error
        jurisdiccionesError,

        // Helpers
        getJurisdiccionLabel,
    };
};
